if (!customElements.get('product-subscription-coupon')) {
  const INSTALLMENT_FORM_ID = 'product-form-installment';
  const INSTALLMENT_SELECTOR = `#${INSTALLMENT_FORM_ID}`;
  const PRODUCT_ADDED_EVENT = 'product:added-to-cart';
  const RECHARGE_SYNC_DELAYS_MS = [50, 150];
  const DEFAULT_DISCOUNT_ERROR = 'Unable to apply discount code. Please try again.';

  const RECHARGE_PRESENCE_SELECTORS = [
    `${INSTALLMENT_SELECTOR} .rc_container`,
    `${INSTALLMENT_SELECTOR} .rc-container`,
    `${INSTALLMENT_SELECTOR} .rc-widget`,
    `${INSTALLMENT_SELECTOR} .rc-option__subsave`,
    `${INSTALLMENT_SELECTOR} .rc_widget__option__input--subsave`,
  ];

  const RECHARGE_EVENT_SELECTORS = [
    `${INSTALLMENT_SELECTOR} .rc_container`,
    `${INSTALLMENT_SELECTOR} .rc-container-wrapper`,
    '.product-inner-info .rc_container',
  ];

  const RECHARGE_ROOT_SELECTORS = [
    `${INSTALLMENT_SELECTOR} .rc_container`,
    `${INSTALLMENT_SELECTOR} .rc-container`,
    INSTALLMENT_SELECTOR,
  ];

  const SUBSAVE_CLICK_SELECTORS = [
    '.rc-option__subsave .rc_widget__option__label',
    '.rc-option__subsave .rc-radio__label',
    '.rc-option__subsave label',
  ];

  customElements.define(
    'product-subscription-coupon',
    class ProductSubscriptionCoupon extends HTMLElement {
      constructor() {
        super();

        this.checkbox = this.querySelector('.product-subscription-coupon__checkbox');
        this.errorEl = this.querySelector('.product-subscription-coupon__error');
        this.infoButton = this.querySelector('.product-subscription-coupon__info');
        this.discountCode = this.dataset.discountCode || '';

        this.isSyncing = false;
        this.discountAppliedToCart = false;
        this.rechargeListenersBound = false;
        this.syncTimeoutIds = [];

        this.onCheckboxChange = this.onCheckboxChange.bind(this);
        this.onRechargeChange = this.onRechargeChange.bind(this);
        this.onRechargeClick = this.onRechargeClick.bind(this);
        this.onInfoClick = this.onInfoClick.bind(this);
        this.onProductAddedToCart = this.onProductAddedToCart.bind(this);
        this.handleRechargePresence = this.handleRechargePresence.bind(this);
        this.syncCouponFromRechargeState = this.syncCouponFromRechargeState.bind(this);
      }

      connectedCallback() {
        if (!this.checkbox) return;

        this.checkbox.addEventListener('change', this.onCheckboxChange);
        this.infoButton?.addEventListener('click', this.onInfoClick);
        document.addEventListener(PRODUCT_ADDED_EVENT, this.onProductAddedToCart);
        this.handleRechargePresence();
        this.observeRechargeWidget();
      }

      disconnectedCallback() {
        this.checkbox?.removeEventListener('change', this.onCheckboxChange);
        this.infoButton?.removeEventListener('click', this.onInfoClick);
        document.removeEventListener(PRODUCT_ADDED_EVENT, this.onProductAddedToCart);
        this.unbindRechargeListener();
        this.clearScheduledSyncs();
        this.rechargeObserver?.disconnect();
        this.selectionObserver?.disconnect();
      }

      getInstallmentForm() {
        return document.getElementById(INSTALLMENT_FORM_ID);
      }

      getRechargeRoot() {
        for (const selector of RECHARGE_ROOT_SELECTORS) {
          const root = document.querySelector(selector);
          if (root) return root;
        }

        return null;
      }

      queryFirst(root, selectors) {
        if (!root) return null;

        for (const selector of selectors) {
          const element = root.querySelector(selector);
          if (element) return element;
        }

        return null;
      }

      isRechargePresent() {
        return RECHARGE_PRESENCE_SELECTORS.some((selector) => document.querySelector(selector));
      }

      isRechargeEventTarget(target) {
        if (!target?.closest) return false;

        return RECHARGE_EVENT_SELECTORS.some((selector) => target.closest(selector));
      }

      isOnetimeTarget(target) {
        if (!target?.classList) return false;

        return (
          target.classList.contains('rc_widget__option__input--onetime') ||
          Boolean(target.closest('.rc-option__onetime'))
        );
      }

      getSubsaveInput() {
        const root = this.getRechargeRoot();

        return this.queryFirst(root, [
          '.rc-option__subsave input',
          '.rc_widget__option__input--subsave',
          '[data-option-subsave] input',
        ]);
      }

      getOnetimeInput() {
        const root = this.getRechargeRoot();

        return this.queryFirst(root, [
          '.rc-option__onetime input',
          '.rc_widget__option__input--onetime',
        ]);
      }

      isSubsaveActive() {
        const root = this.getRechargeRoot();
        if (!root) return false;

        if (this.getSubsaveInput()?.checked) return true;

        return Boolean(root.querySelector('.rc-option__subsave.rc-option--active'));
      }

      isOneTimeActive() {
        const root = this.getRechargeRoot();
        if (!root) return false;

        if (this.getOnetimeInput()?.checked) return true;

        return Boolean(root.querySelector('.rc-option__onetime.rc-option--active'));
      }

      setVisible(isVisible) {
        this.hidden = !isVisible;
        this.classList.toggle('product-subscription-coupon-wrapper--ready', isVisible);
      }

      handleRechargePresence() {
        const isPresent = this.isRechargePresent();
        this.setVisible(isPresent);

        if (!isPresent) return;

        this.bindRechargeListener();
        this.observeRechargeSelection();
      }

      bindRechargeListener() {
        if (this.rechargeListenersBound) return;

        document.addEventListener('change', this.onRechargeChange, true);
        document.addEventListener('click', this.onRechargeClick, true);
        this.rechargeListenersBound = true;
      }

      unbindRechargeListener() {
        if (!this.rechargeListenersBound) return;

        document.removeEventListener('change', this.onRechargeChange, true);
        document.removeEventListener('click', this.onRechargeClick, true);
        this.rechargeListenersBound = false;
      }

      observeRechargeWidget() {
        const form = this.getInstallmentForm();
        if (!form || this.rechargeObserver) return;

        this.rechargeObserver = new MutationObserver(() => {
          this.handleRechargePresence();
        });

        this.rechargeObserver.observe(form, {
          childList: true,
          subtree: true,
        });
      }

      observeRechargeSelection() {
        const container = document.querySelector(`${INSTALLMENT_SELECTOR} .rc_container`);
        if (!container || container.dataset.subscriptionCouponObserved === 'true') return;

        container.dataset.subscriptionCouponObserved = 'true';

        this.selectionObserver = new MutationObserver(() => {
          this.syncCouponFromRechargeState();
        });

        this.selectionObserver.observe(container, {
          attributes: true,
          attributeFilter: ['class'],
          subtree: true,
        });
      }

      clearScheduledSyncs() {
        this.syncTimeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
        this.syncTimeoutIds = [];
      }

      scheduleRechargeSync() {
        this.clearScheduledSyncs();

        window.requestAnimationFrame(() => this.syncCouponFromRechargeState());

        RECHARGE_SYNC_DELAYS_MS.forEach((delay) => {
          const timeoutId = window.setTimeout(() => this.syncCouponFromRechargeState(), delay);
          this.syncTimeoutIds.push(timeoutId);
        });
      }

      onInfoClick(event) {
        const modalSelector = event.currentTarget.getAttribute('data-modal');
        const modal = modalSelector ? document.querySelector(modalSelector) : null;

        if (modal && typeof modal.show === 'function') {
          modal.show(event.currentTarget);
        }
      }

      onRechargeClick(event) {
        if (this.isSyncing || !this.isRechargeEventTarget(event.target)) return;
        if (!this.isOnetimeTarget(event.target)) return;

        this.scheduleRechargeSync();
      }

      onRechargeChange(event) {
        if (this.isSyncing || !this.isRechargeEventTarget(event.target)) return;

        window.requestAnimationFrame(() => this.syncCouponFromRechargeState());
      }

      syncCouponFromRechargeState() {
        if (this.isSyncing || !this.checkbox.checked) return;

        if (this.isOneTimeActive()) {
          this.uncheckCouponFromRecharge();
        }
      }

      uncheckCouponFromRecharge() {
        if (!this.checkbox.checked) return;

        this.isSyncing = true;
        this.checkbox.checked = false;

        this.clearCouponState().finally(() => {
          this.isSyncing = false;
        });
      }

      async onCheckboxChange() {
        if (this.isSyncing) return;

        this.clearError();

        if (this.checkbox.checked) {
          this.isSyncing = true;

          try {
            if (!this.isSubsaveActive()) {
              this.selectSubsaveOption();
            }
          } finally {
            this.isSyncing = false;
          }

          return;
        }

        this.isSyncing = true;

        try {
          await this.clearCouponState();
        } finally {
          this.isSyncing = false;
        }
      }

      async clearCouponState() {
        this.clearError();
        this.discountAppliedToCart = false;

        try {
          if (await this.shouldKeepCartDiscount()) return;

          await this.removeCartDiscount();
        } catch (error) {
          this.showError(error.message);
        }
      }

      getCartJsonUrl() {
        if (typeof routes !== 'undefined' && routes.cart_url) {
          return `${routes.cart_url}.js`;
        }

        if (typeof routes_cart_url !== 'undefined') {
          return `${routes_cart_url}.js`;
        }

        return '/cart.js';
      }

      async fetchCart() {
        const response = await fetch(this.getCartJsonUrl());

        if (!response.ok) {
          throw new Error('Unable to load cart.');
        }

        return response.json();
      }

      cartHasSubscriptionItems(cart) {
        return cart.items?.some((item) => item.selling_plan_allocation != null) ?? false;
      }

      countSubscriptionItems(cart) {
        return cart.items?.filter((item) => item.selling_plan_allocation != null).length ?? 0;
      }

      async shouldKeepCartDiscount() {
        const cart = await this.fetchCart();
        return this.cartHasSubscriptionItems(cart);
      }

      async onProductAddedToCart() {
        if (!this.discountCode) return;

        if (this.checkbox.checked) {
          try {
            await this.applyCartDiscount(this.discountCode);
            this.discountAppliedToCart = true;
            this.clearError();
          } catch (error) {
            this.showError(error.message);
          }

          return;
        }

        if (!this.isSubsaveActive()) return;

        try {
          const cart = await this.fetchCart();

          if (this.countSubscriptionItems(cart) !== 1) return;

          await this.removeCartDiscount();
          this.discountAppliedToCart = false;
          this.clearError();
        } catch (error) {
          this.showError(error.message);
        }
      }

      selectSubsaveOption() {
        const root = this.getRechargeRoot();
        if (!root) return;

        const actions = [
          ...SUBSAVE_CLICK_SELECTORS.map(
            (selector) => () => root.querySelector(selector)?.click()
          ),
          () => this.getSubsaveInput()?.click(),
        ];

        const activateSubsave = () => {
          for (const action of actions) {
            if (this.isSubsaveActive()) return true;
            action();
            if (this.isSubsaveActive()) return true;
          }

          return this.isSubsaveActive();
        };

        if (activateSubsave()) return;

        window.requestAnimationFrame(() => {
          if (!activateSubsave()) {
            window.setTimeout(activateSubsave, 100);
          }
        });
      }

      getCartUpdateUrl() {
        if (typeof routes !== 'undefined' && routes.cart_update_url) {
          return routes.cart_update_url;
        }

        return '/cart/update.js';
      }

      getCartDrawer() {
        return document.querySelector('cart-drawer');
      }

      buildDiscountPayload(code) {
        const payload = { discount: code };
        const cart = this.getCartDrawer();

        if (cart?.getSectionsToRender) {
          payload.sections = cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }

        return payload;
      }

      parseDiscountError(data, response) {
        if (!response.ok) {
          return data?.description || data?.message || DEFAULT_DISCOUNT_ERROR;
        }

        if (data?.status && data.status !== 200) {
          return data.description || DEFAULT_DISCOUNT_ERROR;
        }

        return null;
      }

      async updateCartDiscount(code) {
        const response = await fetch(this.getCartUpdateUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(this.buildDiscountPayload(code)),
        });

        const data = await response.json();
        const errorMessage = this.parseDiscountError(data, response);

        if (errorMessage) {
          throw new Error(errorMessage);
        }

        const cart = this.getCartDrawer();
        if (cart?.renderContents && data.sections) {
          cart.renderContents(data);
        }

        return data;
      }

      applyCartDiscount(code) {
        return this.updateCartDiscount(code);
      }

      removeCartDiscount() {
        return this.updateCartDiscount('');
      }

      showError(message) {
        if (!this.errorEl) return;

        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      clearError() {
        if (!this.errorEl) return;

        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }
    }
  );
}